import { apiGet } from './client';
import type { OperationResult } from './types';

// Mirrors OO.Business.AIInfo.Interfaces.ActivityEvent.

export type ActivityKind = 'llm' | 'tool' | 'consult' | 'retrieval';

export interface ActivityEvent {
  kind: ActivityKind;
  id: number;
  timestamp: string;            // ISO
  agentId?: number | null;
  agentName?: string | null;
  durationMs: number;
  errored: boolean;
  errorMessage?: string | null;
  correlationId?: string | null;

  // LLM-specific
  modelName?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  cachedTokens?: number | null;
  estimatedCostUsd?: number | null;
  percentOfContext?: number | null;
  alertSeverity?: string | null;
  toolCallCount?: number | null;
  compactionApplied?: boolean | null;
  threadId?: string | null;
  inputPrompt?: string | null;
  outputPrompt?: string | null;
  toolNames?: string | null;

  // Tool-specific
  pluginName?: string | null;
  methodName?: string | null;
  argumentChars?: number | null;
  responseChars?: number | null;
  modelMaxOutputTokens?: number | null;
  argumentsHash?: string | null;
  llmPromptLogId?: number | null;

  // Consult-specific
  primaryAgentName?: string | null;
  peerAgentId?: number | null;
  peerAgentName?: string | null;
  consultationDepth?: number | null;
  timedOut?: boolean | null;
  question?: string | null;
  response?: string | null;

  // Retrieval-specific (RAG / Azure AI Search)
  retrievalQuery?: string | null;
  indexName?: string | null;
  resultCount?: number | null;
  topScore?: number | null;
  zeroResults?: boolean | null;

  // Grounding-budget (LLM-specific, added 2026-05-03)
  systemPromptTokens?: number | null;
  baseInstructionTokens?: number | null;

  // Per-turn skill manifest (LLM-specific). Populated when the writer
  // captured ai.PromptLogSkillLoad rows for this turn.
  loadedSkills?: LoadedSkillDto[] | null;

  // Per-turn briefing manifest (LLM-specific, added 2026-05-22). Grouped
  // by (BriefingCode, BriefingVersion); each briefing carries its element
  // rows including dedup + fault metadata.
  loadedBriefings?: LoadedBriefingDto[] | null;

  issueTags: string[];
}

/** One row from ai.PromptLogSkillLoad surfaced inside an ActivityEvent. */
export interface LoadedSkillDto {
  skillId: number;
  skillName: string;
  /** AlwaysLoad / AgentLink / RouterPicked / Inline / Advertised / AuditFallback. */
  loadSource: string;
}

/**
 * One briefing's worth of rendered elements from
 * `ai.PromptLogBriefingContext`, grouped by (BriefingCode, BriefingVersion).
 * The drawer renders one row per briefing with an expandable element list.
 */
export interface LoadedBriefingDto {
  briefingCode: string;
  /** Resolved from ai.BriefingContext.Name; falls back to briefingCode. */
  briefingName: string;
  briefingVersion?: number | null;
  elements: LoadedBriefingElementDto[];
}

/** One row from ai.PromptLogBriefingContext within a LoadedBriefingDto. */
export interface LoadedBriefingElementDto {
  elementCode?: string | null;
  /** Resolved from ai.BriefingContextElement.DisplayName; falls back to elementCode. */
  elementDisplayName?: string | null;
  /** 1 (Stable) or 2 (Per-turn). Null on briefing-level fault rows. */
  elementTier?: number | null;
  tokenEstimate: number;
  /** Non-null = element was deduped; value = winning briefing's code. */
  dedupedFromCode?: string | null;
  isFault: boolean;
  /** Factory / Provider / Manager / Renderer / FeatureFlagOff / CircuitOpen. */
  faultStage?: string | null;
  faultType?: string | null;
}

export interface ActivityRecentParams {
  // Either pick a relative window (`hours`) or send an absolute custom
  // range (`fromUtc` + `toUtc`, ISO strings). If both are given, the
  // server prefers the absolute range. `days` is the legacy fallback.
  hours?: number;
  fromUtc?: string;
  toUtc?: string;
  days?: number;
  agentId?: number;
  onlyIssues?: boolean;
  pageSize?: number;
}

export async function fetchRecent(params: ActivityRecentParams = {}): Promise<ActivityEvent[]> {
  const query: Record<string, unknown> = {
    pageSize: params.pageSize ?? 200,
  };
  if (params.fromUtc) {
    query.fromUtc = params.fromUtc;
    if (params.toUtc) query.toUtc = params.toUtc;
  } else if (params.hours != null) {
    query.hours = params.hours;
  } else {
    query.days = params.days ?? 1;
  }
  if (params.agentId) query.agentId = params.agentId;
  if (params.onlyIssues) query.onlyIssues = true;

  const wrapped = await apiGet<OperationResult<ActivityEvent[]>>('/api/Activity/Recent', query);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load activity');
  return wrapped.data ?? [];
}

export interface TurnHypothesis {
  hypothesis?: string | null;
  suggestedAction?: string | null;
  confidence?: 'low' | 'medium' | 'high' | string | null;
  keyIssues?: string[];
  eventCount?: number;
  generatedAtUtc?: string;
  analyzerModel?: string;
  isEmpty?: boolean;
}

export async function fetchHypothesis(params: {
  correlationId?: string;
  threadId?: string;
}): Promise<TurnHypothesis> {
  const wrapped = await apiGet<OperationResult<TurnHypothesis>>(
    '/api/Activity/AnalyzeTurn',
    {
      ...(params.correlationId ? { correlationId: params.correlationId } : {}),
      ...(params.threadId ? { threadId: params.threadId } : {}),
    },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to analyze turn');
  }
  return wrapped.data;
}

export async function fetchTrace(correlationId: string): Promise<ActivityEvent[]> {
  const wrapped = await apiGet<OperationResult<ActivityEvent[]>>('/api/Activity/Trace', {
    correlationId,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load trace');
  return wrapped.data ?? [];
}

export async function fetchByThread(threadId: string): Promise<ActivityEvent[]> {
  const wrapped = await apiGet<OperationResult<ActivityEvent[]>>('/api/Activity/ByThread', {
    threadId,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load thread events');
  return wrapped.data ?? [];
}

// ── App Insights spans for ONE correlation id ────────────────────────────
// Pulls exceptions + dependencies from App Insights for a single turn so
// the Investigation Drawer can show framework-level context (Azure OpenAI
// HTTP timing, unhandled exception stacks) alongside our SQL log rows.

export interface ExceptionEvent {
  timestamp: string;
  type?: string | null;
  outerMessage?: string | null;
  cloudRoleName?: string | null;
  operationId?: string | null;
  customDimensions?: Record<string, string>;
}

export interface DependencySpan {
  spanId?: string | null;
  parentSpanId?: string | null;
  timestamp: string;
  name?: string | null;
  type?: string | null;
  target?: string | null;
  resultCode?: string | null;
  success: boolean;
  durationMs: number;
  cloudRoleName?: string | null;
}

export interface TraceTelemetry {
  requests?: DependencySpan[];
  truncated?: boolean;
  operationId?: string | null;
  exceptions: ExceptionEvent[];
  dependencies: DependencySpan[];
}

export async function fetchTraceTelemetry(correlationId: string): Promise<TraceTelemetry> {
  const wrapped = await apiGet<OperationResult<TraceTelemetry>>(
    '/api/Activity/TraceTelemetry',
    { correlationId },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load execution telemetry');
  }
  return {
    operationId: wrapped.data.operationId ?? null,
    exceptions: wrapped.data.exceptions ?? [],
    dependencies: wrapped.data.dependencies ?? [],
    requests: wrapped.data.requests ?? [],
    truncated: wrapped.data.truncated ?? false,
  };
}

export interface ActivityStatus {
  appInsightsConfigured: boolean;
}

export async function fetchStatus(): Promise<ActivityStatus> {
  const wrapped = await apiGet<OperationResult<ActivityStatus>>('/api/Activity/Status');
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load status');
  return wrapped.data ?? { appInsightsConfigured: false };
}
